import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { faChevronDown, faChevronUp, faCopy, faUpload } from '@fortawesome/free-solid-svg-icons';
import { OfflineService } from '@evorait/pace-offline';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';
import { ReportIssueComponent } from '../report-issue/report-issue.component';
import { ReportIssueModel } from '../report-issue/report-issue.model';
import { HttpErrorResponse } from '@angular/common/http';
import { safeStringify } from '@evorait/pace-components';
import { BackendErrorDetails } from '../../services/config/backend-error-messages.config';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

@Component({
    selector: 'pace-core-backend-error-message-modal',
    templateUrl: './backend-error-message-modal.component.html',
    styleUrls: ['./backend-error-message-modal.component.scss'],
    standalone: false,
})
export class BackendErrorMessageModalComponent implements OnInit {
    @Input({ required: true })
    public errorData: {
        customErrorDetails: BackendErrorDetails;
        rawError: HttpErrorResponse;
    };
    @Input() public supportId?: string;

    public isDetailExpanded = false;
    public pendingRequestCount$: Observable<number>;

    public readonly faUpload = faUpload;
    public readonly faChevronUp = faChevronUp;
    public readonly faChevronDown = faChevronDown;
    public readonly faCopy = faCopy;

    public constructor(
        public readonly modalInstance: BsModalRef,
        private readonly modalService: BsModalService,
        private offlineService: OfflineService,
        private readonly translate: TranslateService,
    ) {}

    public ngOnInit(): void {
        this.pendingRequestCount$ = this.offlineService.pendingRequestCount$;

        this.normalizeErrorData();
    }

    private normalizeErrorData(): void {
        const rawError = this.errorData.rawError ?? {};
        const customError = this.errorData.customErrorDetails ?? {};
        this.errorData.customErrorDetails = {
            message: customError.message ?? this.translate.instant('ERROR_MESSAGE_MODAL_DEFAULT_MESSAGE'),
            action: customError.action ?? this.translate.instant('ERROR_MESSAGE_MODAL_DEFAULT_ACTION'),
        };

        this.errorData.rawError = new HttpErrorResponse({
            url: rawError.url ?? <string>this.translate.instant('ERROR_MESSAGE_MODAL_DEFAULT_URL'),
            error: rawError.error ?? '',
            status: rawError.status ?? 0,
            statusText:
                rawError.statusText ?? <string>this.translate.instant('ERROR_MESSAGE_MODAL_DEFAULT_STATUS_TEXT'),
        });
    }

    public reportAnIssue(): void {
        this.modalInstance.hide();
        let message = this.errorData.customErrorDetails.message;
        if (this.supportId) {
            message += `\n\n(Support Id: ${this.supportId})`;
        }
        this.modalService.show(ReportIssueComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new ReportIssueModel(message),
            },
            ariaLabelledBy: 'report-issue-modal-title',
        });
    }

    public close(): void {
        this.modalInstance.hide();
    }

    public toggleDetailsExpansion(): void {
        this.isDetailExpanded = !this.isDetailExpanded;
    }

    public get formattedError(): string {
        const error = this.errorData.rawError.error;

        if (!error) {
            return '';
        }

        let errorObj = error;

        // Deeply parse the error object so that any stringified JSON payloads inside are formatted nicely.
        // Needs to be typed carefully as unknown to prevent typescript lint issues while iterating
        const deepParseJsonStrings = (obj: unknown): unknown => {
            if (typeof obj === 'string') {
                try {
                    const parsed = JSON.parse(obj);
                    // Prevent infinite loops or parsing simple numbers/booleans that look like JSON strings
                    if (typeof parsed === 'object' && parsed !== null) {
                        return deepParseJsonStrings(parsed);
                    }
                } catch {
                    // Not valid JSON string, ignore
                }
                return obj;
            } else if (Array.isArray(obj)) {
                return obj.map((item) => deepParseJsonStrings(item));
            } else if (typeof obj === 'object' && obj !== null) {
                const newObj: Record<string, unknown> = {};
                for (const key of Object.keys(obj)) {
                    newObj[key] = deepParseJsonStrings((obj as Record<string, unknown>)[key]);
                }
                return newObj;
            }
            return obj;
        };

        errorObj = deepParseJsonStrings(errorObj);

        if (typeof errorObj === 'object' && errorObj !== null) {
            return safeStringify(errorObj, 2);
        } else {
            return String(errorObj);
        }
    }

    public copyOperationIdQuery(): void {
        if (!this.supportId) {
            return;
        }
        navigator.clipboard.writeText(this.supportId).catch((err) => {
            console.error('Failed to copy to clipboard', err);
        });
    }

    public copyEntireError(): void {
        const raw = this.errorData?.rawError;
        let textToCopy = '';
        if (raw) {
            textToCopy += `${raw.name} for route:\n${raw.url}\nStatus: ${raw.status} ${raw.statusText}\n\n`;
        }
        textToCopy += this.formattedError;

        navigator.clipboard.writeText(textToCopy).catch((err) => {
            console.error('Failed to copy error to clipboard', err);
        });
    }
}
